import { Direction } from '@modules/common/types';
import { directionToString } from '@modules/common/utils';
import { MEDIA_WIDGET_MIN_HEIGHT } from '@modules/dashboard/constants';
import {
  addCellToDashboardLayout,
  getNewCellID,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks/useUpsertDashboard';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DashboardCell } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useSetDashboardActiveCellId } from './useSetDashboardActiveCellId';

interface useAddMediaWidgetParams {
  rowID?: string;
  cellInsertDirection: Direction;
}

export function useAddMediaWidget({
  rowID,
  cellInsertDirection,
}: useAddMediaWidgetParams) {
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const { upsertDashboard } = useUpsertDashboard();
  const setDashboardActiveCellId = useSetDashboardActiveCellId();
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );

  const addMediaContent = async ({
    videoLink,
    imageLink,
  }: {
    videoLink?: string;
    imageLink?: string;
  }): Promise<DashboardCell> => {
    const clonedMetadata = cloneDeep(metadata);

    const contentID = uuidv4();
    const cell = {
      id: getNewCellID(),
      columnSpan: 12,
      contentID,
    };

    const { destinationRowID } = addCellToDashboardLayout({
      cell,
      rowID,
      cellInsertDirection,
      layout: clonedMetadata.layout,
    });

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: clonedMetadata,
    });

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        cellMinimumHeightsByRowID: {
          ...cellMinimumHeightsByRowID,
          [destinationRowID]: {
            ...cellMinimumHeightsByRowID[destinationRowID],
            [cell.id]: MEDIA_WIDGET_MIN_HEIGHT,
          },
        },
      },
    });

    setDashboardActiveCellId(cell.id);

    await upsertDashboard({
      dashboardProps: {
        metadataV2: clonedMetadata,
        addWidget: {
          id: contentID,
          imageWidget:
            videoLink === undefined ? { url: imageLink ?? '' } : undefined,
          videoWidget:
            imageLink === undefined ? { url: videoLink ?? '' } : undefined,
          cellIdentifier: {
            cellId: cell.id,
            rowId: destinationRowID,
          },
        },
      },
      shouldRefetchDashboard: true,
    });

    trackDashboardEvent({
      eventName: 'Dashboard Add Content',
      placement: directionToString(cellInsertDirection),
      type: 'media',
      from: 'Dashboard Page',
    });

    return cell;
  };

  return addMediaContent;
}
